import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import Select from "react-select";

import { API_URL } from "../../../config";
import "./UpdateProject.css";

const UpdateProject = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [project, setProject] = useState({
    projectName: "",
    department: "",
    service: "",
    price: "",
    startDate: "",
    endDate: "",
    status: "Pending",
    projectCategory: [],
    notes: "",
    projectDescription: "",
    addMember: []
  });

  const [departments, setDepartments] = useState([]);
  const [services, setServices] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const projectRes = await axios.get(
          `${API_URL}/api/getprojectByPorjectId/${id}`
        );
        const projectData = projectRes.data;

        setProject({
          projectName: projectData.projectName,
          department: projectData.department?._id || "",
          service: projectData.service?._id || "",
          price: projectData.price || "",
          startDate: projectData.startDate?.slice(0, 10) || "",
          endDate: projectData.endDate?.slice(0, 10) || "",
          status: projectData.status || "Pending",
          projectCategory: projectData.projectCategory || [],
          notes: projectData.notes || "",
          projectDescription: projectData.projectDescription || "",
          addMember: projectData.addMember
            ? projectData.addMember.map((m) => ({ value: m._id, label: m.ename }))
            : []
        });

        const deptRes = await axios.get(`${API_URL}/api/getDepartment`);
        setDepartments(deptRes.data || []);
      } catch (err) {
        console.error("Error fetching project or departments:", err);
      }
    };

    fetchData();
  }, [id]);

  useEffect(() => {
    if (!project.department) {
      setServices([]);
      setEmployees([]);
      return;
    }

    const fetchDeptData = async () => {
      try {
        const [serviceRes, empRes] = await Promise.all([
          axios.get(`${API_URL}/api/getServicebyDepartment/${project.department}`),
          axios.get(`${API_URL}/api/getEmployeeByDepartment/${project.department}`)
        ]);

        setServices(serviceRes.data || []);
        setEmployees(empRes.data.map((emp) => ({ value: emp._id, label: emp.ename })));
        setLoading(false);
      } catch (err) {
        console.error("Error fetching services/employees:", err);
        setLoading(false);
      }
    };

    fetchDeptData();
  }, [project.department]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setProject({ ...project, [name]: value });
  };

  const handleCategoryChange = (e) => {
    setProject({ ...project, projectCategory: e.target.value.split(",") });
  };

  const handleMemberChange = (selectedOptions) => {
    setProject({ ...project, addMember: selectedOptions });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API_URL}/api/updateProject/${id}`, {
        ...project,
        addMember: project.addMember.map((m) => m.value)
      });
      alert("Project updated successfully!");
      navigate(`/admin/getAllprojects/${id}`);
    } catch (err) {
      console.error("Update failed:", err);
      alert(err?.response?.data?.message || err?.response?.data?.error || "Failed to update project");
    }
  };

  if (loading) return <p className="update-project-loading">Loading project...</p>;

  return (
    <div className="update-project-page">
      <div className="update-project-shell">
        <div className="update-project-header">
          <p className="update-project-eyebrow">Project Management</p>
          <h3>Update Project</h3>
          <span>Update project details while keeping existing mapping and members.</span>
        </div>

        <form className="update-project-card" onSubmit={handleSubmit}>
          <div className="update-project-grid">
            <div className="update-project-field update-project-field-full">
              <label className="form-label">Project Name</label>
              <input
                type="text"
                className="form-control update-project-input"
                name="projectName"
                value={project.projectName}
                onChange={handleChange}
                required
              />
            </div>

            <div className="update-project-field">
              <label className="form-label">Department</label>
              <select
                className="form-select update-project-input"
                name="department"
                value={project.department}
                onChange={handleChange}
              >
                <option value="">Select Department</option>
                {departments.map((dept) => (
                  <option key={dept._id} value={dept._id}>
                    {dept.deptName}
                  </option>
                ))}
              </select>
            </div>

            <div className="update-project-field">
              <label className="form-label">Service</label>
              <select
                className="form-select update-project-input"
                name="service"
                value={project.service}
                onChange={handleChange}
              >
                <option value="">Select Service</option>
                {services.map((srv) => (
                  <option key={srv._id} value={srv._id}>
                    {srv.serviceName}
                  </option>
                ))}
              </select>
            </div>

            <div className="update-project-field">
              <label className="form-label">Start Date</label>
              <input
                type="date"
                className="form-control update-project-input"
                name="startDate"
                value={project.startDate}
                onChange={handleChange}
              />
            </div>

            <div className="update-project-field">
              <label className="form-label">End Date</label>
              <input
                type="date"
                className="form-control update-project-input"
                name="endDate"
                value={project.endDate}
                onChange={handleChange}
              />
            </div>

            <div className="update-project-field">
              <label className="form-label">Project Status</label>
              <select
                className="form-select update-project-input"
                name="status"
                value={project.status}
                onChange={handleChange}
              >
                <option value="Pending">Pending</option>
                <option value="In Progress">In Progress</option>
                <option value="Completed">Completed</option>
              </select>
            </div>

            <div className="update-project-field update-project-field-full">
              <label className="form-label">Category</label>
              <input
                type="text"
                className="form-control update-project-input"
                value={project.projectCategory.join(",")}
                onChange={handleCategoryChange}
                placeholder="Comma separated (e.g. React,Node)"
              />
            </div>

            <div className="update-project-field update-project-field-full">
              <label className="form-label">Budget (Price)</label>
              <input
                type="number"
                className="form-control update-project-input"
                name="price"
                value={project.price}
                onChange={handleChange}
              />
            </div>

            <div className="update-project-field update-project-field-full">
              <label className="form-label">Assign Members</label>
              <Select
                isMulti
                options={employees}
                value={project.addMember}
                onChange={handleMemberChange}
                classNamePrefix="update-project-select"
              />
            </div>

            <div className="update-project-field update-project-field-full">
              <label className="form-label">Notes</label>
              <textarea
                className="form-control update-project-input"
                name="notes"
                rows="3"
                value={project.notes}
                onChange={handleChange}
              />
            </div>

            <div className="update-project-field update-project-field-full">
              <label className="form-label">Description</label>
              <textarea
                className="form-control update-project-input"
                name="projectDescription"
                rows="4"
                value={project.projectDescription}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="update-project-actions">
            <button
              type="button"
              className="btn update-project-btn update-project-btn-muted"
              onClick={() => navigate(`/admin/getAllprojects/${id}`)}
            >
              Cancel
            </button>
            <button type="submit" className="btn update-project-btn update-project-btn-primary">
              Save Changes
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UpdateProject;
